
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Sidebar from "../../components/dashboard/Sidebar";

const ProfileScreen = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [toast, setToast] = useState(null);

  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        navigate("/login");
        return;
      }
      setUser(u);
      try {
        const snap = await getDoc(doc(db, "internshipusers", u.uid));
        if (snap.exists()) {
          setUserData(snap.data());
        }
      } catch (e) { 
        console.error("Fetch user error:", e); 
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const showToast = (msg, type) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleUpdatePassword = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast("New passwords do not match!", "warn");
      return;
    }
    showToast("Password updated successfully!", "success");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  if (loading) {
    return (
      <div style={{ display:"flex", height:"100vh", alignItems:"center", justifyContent:"center", background:"#F3F4F6" }}>
        <div style={{ color: "#7C3AED", fontWeight: 700, fontSize: 18 }}>Loading Profile...</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100vh", backgroundColor: "#F3F4F6", overflow: "hidden" }}>
      <Sidebar user={user} userData={userData} collapsed={collapsed} setCollapsed={setCollapsed} />
      
      <main style={{ flex: 1, overflowY: "auto", padding: "40px", fontFamily: "'Outfit', sans-serif" }}>
        {toast && (
          <div style={{
            position: "fixed", top: 24, right: 28, zIndex: 9999,
            background: "rgba(255,255,255,0.97)",
            border: `2px solid ${toast.type === "success" ? "#10B981" : "#F59E0B"}`,
            borderRadius: 16, padding: "14px 22px", boxShadow: "0 16px 48px rgba(109,40,217,0.18)",
            fontSize: 13, fontWeight: 600, color: "#4C1D95",
          }}>
            {toast.msg}
          </div>
        )}

        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h1 style={{ fontSize: "32px", fontWeight: "800", color: "#4C1D95", marginBottom: "8px" }}>My Profile</h1>
          <p style={{ color: "#6B7280", marginBottom: "32px" }}>Manage your personal details and security settings.</p>

          <div style={{ background: "#FFFFFF", borderRadius: "20px", padding: "32px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)", marginBottom: "32px" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#4C1D95", marginBottom: "24px", borderBottom: "1px solid #E5E7EB", paddingBottom: "12px" }}>Basic Details</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
              <div>
                <label style={{ display: "block", fontSize: "14px", color: "#6B7280", marginBottom: "8px", fontWeight: "600" }}>Full Name</label>
                <div style={{ padding: "12px 16px", background: "#F9FAFB", borderRadius: "12px", border: "1px solid #E5E7EB", color: "#1F2937", fontWeight: "500" }}>
                  {userData?.firstName || "User"} {userData?.lastName || ""}
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", color: "#6B7280", marginBottom: "8px", fontWeight: "600" }}>Email Address</label>
                <div style={{ padding: "12px 16px", background: "#F9FAFB", borderRadius: "12px", border: "1px solid #E5E7EB", color: "#1F2937", fontWeight: "500" }}>
                  {userData?.email || user?.email || "Not Provided"}
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", color: "#6B7280", marginBottom: "8px", fontWeight: "600" }}>Phone Number</label>
                <div style={{ padding: "12px 16px", background: "#F9FAFB", borderRadius: "12px", border: "1px solid #E5E7EB", color: "#1F2937", fontWeight: "500" }}>
                  {userData?.phone || userData?.contactNumber || "Not Provided"}
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", color: "#6B7280", marginBottom: "8px", fontWeight: "600" }}>Role</label>
                <div style={{ padding: "12px 16px", background: "#F9FAFB", borderRadius: "12px", border: "1px solid #E5E7EB", color: "#1F2937", fontWeight: "500" }}>
                  {userData?.role || "Student"}
                </div>
              </div>
            </div>
          </div>

          <div style={{ background: "#FFFFFF", borderRadius: "20px", padding: "32px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#4C1D95", marginBottom: "24px", borderBottom: "1px solid #E5E7EB", paddingBottom: "12px" }}>Security Settings</h2>
            <form onSubmit={handleUpdatePassword}>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", fontSize: "14px", color: "#6B7280", marginBottom: "8px", fontWeight: "600" }}>Current Password</label>
                <input 
                  type="password" 
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{ width: "100%", padding: "12px 16px", borderRadius: "12px", border: "1px solid #E5E7EB", outline: "none" }}
                  required
                />
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", fontSize: "14px", color: "#6B7280", marginBottom: "8px", fontWeight: "600" }}>New Password</label>
                <input 
                  type="password" 
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ width: "100%", padding: "12px 16px", borderRadius: "12px", border: "1px solid #E5E7EB", outline: "none" }}
                  required
                />
              </div>
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontSize: "14px", color: "#6B7280", marginBottom: "8px", fontWeight: "600" }}>Confirm New Password</label>
                <input 
                  type="password" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ width: "100%", padding: "12px 16px", borderRadius: "12px", border: "1px solid #E5E7EB", outline: "none" }}
                  required
                />
              </div>
              <button 
                type="submit"
                style={{ background: "linear-gradient(135deg, #7C3AED, #A78BFA)", color: "#fff", padding: "14px 24px", border: "none", borderRadius: "12px", fontSize: "16px", fontWeight: "700", cursor: "pointer", width: "100%", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}
              >
                Update Password
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileScreen;
